import moment from "moment";
import { theme } from "nectar-ui";
import {
  CHART_COLORS_PALLETE,
  DASHBOARD_CHART_TYPES,
  DASHBOARD_RANGE_TYPES_DICTIONARY,
  DASHBOARD_RANGE_TYPE_KEYS,
} from "@utils/constants/dashboard/dashboardModuleConstants";
import {
  apexChartInvertedSeriesClickHandler,
  getXAxisTooltipFormatterBasedOnRange,
} from "@utils/helpers/dashboardHelpers";
import { ApexOptions } from "apexcharts";

export const MAXIMUM_RESPONSIBLE_NUMBER = 8;
export const MINIMUM_RESPONSIBLE_NUMBER = 1;

type GetFromAndToDate = {
  (
    dateRange: { fromDate: moment.Moment; toDate: moment.Moment } | string,
    formatTemplate?: string
  ): { fromDate: string; toDate: string };
};

export const getFromAndToDate: GetFromAndToDate = (dateRange, formatTemplate = "YYYY-MM-DD") => {
  if (typeof dateRange !== "string")
    return {
      fromDate: dateRange.fromDate.format(formatTemplate),
      toDate: dateRange.toDate.format(formatTemplate),
    };

  return DASHBOARD_RANGE_TYPES_DICTIONARY[dateRange as DASHBOARD_RANGE_TYPE_KEYS].range;
};

export const getChartOptions = (
  xAxis: string[],
  groups: { title: string; cols: number }[],
  rangeFilter: string
): ApexOptions => {
  return {
    chart: {
      id: DASHBOARD_CHART_TYPES.DONE_ACTIVITIES_BY_RESPONSIBLE.chart,
      type: "line",
      fontFamily: theme.nectar.fontStack.default,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      events: {
        legendClick: apexChartInvertedSeriesClickHandler,
      },
    },
    plotOptions: {},
    colors: CHART_COLORS_PALLETE,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 3,
      curve: "straight",
    },
    title: {
      text: "",
      align: "left",
    },
    legend: {
      horizontalAlign: "center",
      onItemClick: {
        toggleDataSeries: false,
      },
      markers: {
        width: 16,
        height: 16,
        radius: 2,
      },
    },
    tooltip: {
      x: {
        formatter: getXAxisTooltipFormatterBasedOnRange,
      },
      items: {},
    },
    markers: {
      size: 0,
      hover: {
        sizeOffset: 6,
      },
    },
    xaxis: {
      categories: xAxis,
      tickAmount: 15,
      tickPlacement: "between",
      labels: {
        hideOverlappingLabels: false,
        rotate: 0,
        rotateAlways: false,
        style: {
          fontSize: "14px",
          fontWeight: 300,
          colors: theme.nectar.colors.gray500,
        },
        formatter: (date: moment.MomentInput) => {
          const [dayString, monthString] = moment(date, "YYYY-MM-DD").format("D MMM").split(" ");
          const formattedMonthString =
            monthString.charAt(0).toUpperCase() + monthString.substring(1);

          if (rangeFilter === "LAST_YEAR") return formattedMonthString;
          return `${dayString} ${formattedMonthString}`;
        },
      },
      group: {
        groups,
        style: {
          fontSize: "14px",
          fontWeight: 300,
          colors: theme.nectar.colors.gray500,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => {
          return Math.round(val).toString();
        },
        style: {
          fontSize: "14px",
          fontWeight: 500,
          colors: theme.nectar.colors.gray500,
        },
      },
    },
    grid: {
      borderColor: "#DEE2E6",
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          xaxis: {
            tickAmount: 3,
          },
        },
      },
    ],
  };
};
